import Color from 'color';
import stringHash from 'string-hash';
export var Gradient;
(function (Gradient) {
    Gradient["flare"] = "flare";
    Gradient["horizon"] = "horizon";
    Gradient["aurora"] = "aurora";
    Gradient["galaxy"] = "galaxy";
    Gradient["gravity"] = "gravity";
    Gradient["nebula"] = "nebula";
    Gradient["grey"] = "grey";
})(Gradient || (Gradient = {}));
export function createPalette(config) {
    const combinedPalette = { ...DefaultPaletteConfig, ...config };
    const shades = calcShades(combinedPalette.primary, combinedPalette.secondary);
    return {
        default: shades[0],
        primary: combinedPalette.primary,
        shade7: shades[0],
        shade6: shades[1],
        shade5: shades[2],
        shade4: shades[3],
        shade3: shades[4],
        shade2: shades[5],
        shade1: shades[6],
        secondary: combinedPalette.secondary,
        success: combinedPalette.success,
        info: combinedPalette.info,
        warning: combinedPalette.warning,
        error: combinedPalette.error,
        brand1: combinedPalette.brand1,
        brand2: combinedPalette.brand2,
        accent1: combinedPalette.accent1,
        accent2: combinedPalette.accent2,
        disabled: darken(shades[0], 0.2),
    };
}
export function createSchemes(config) {
    const palette = createPalette(config);
    const disabledScheme = createScheme(palette.disabled);
    disabledScheme.text = palette.shade6;
    return {
        default: createScheme(palette.default),
        primary: createScheme(palette.primary),
        shade7: createScheme(palette.shade7),
        shade6: createScheme(palette.shade6),
        shade5: createScheme(palette.shade5),
        shade4: createScheme(palette.shade4),
        shade3: createScheme(palette.shade3),
        shade2: createScheme(palette.shade2),
        shade1: createScheme(palette.shade1),
        secondary: createScheme(palette.secondary),
        success: createScheme(palette.success),
        info: createScheme(palette.info),
        warning: createScheme(palette.warning),
        error: createScheme(palette.error),
        brand1: createScheme(palette.brand1),
        brand2: createScheme(palette.brand2),
        accent1: createScheme(palette.accent1),
        accent2: createScheme(palette.accent2),
        disabled: disabledScheme,
    };
}
export function transparentize(colorValue, opacity) {
    const color = new Color(colorValue);
    return color.fade(opacity).rgb().string();
}
export function darken(colorValue, strength) {
    const color = new Color(colorValue);
    return color.darken(strength).rgb().string();
}
export function lighten(colorValue, strength) {
    const color = new Color(colorValue);
    return color.lighten(strength).rgb().string();
}
export function LinearGradient(deg, from, to) {
    return `linear-gradient(${deg}deg, ${from}, ${to})`;
}
export function getContrastingTextColor(colorHex) {
    const light = new Color('#ffffff');
    const dark = new Color('#000000');
    const color = new Color(colorHex);
    const lightTextContrast = color.contrast(light);
    const darkTextContrast = color.contrast(dark);
    if (lightTextContrast > darkTextContrast) {
        return light.toString();
    }
    return dark.toString();
}
//// "Private" Functions ////
export function calcShades(primary, secondary) {
    const dark = new Color(primary);
    const light = new Color(secondary);
    const shades = [];
    const a = 0.9;
    const b = 5;
    const c = 3;
    const gaus = (x) => a * Math.exp(-((x - b) ** 2 / (2 * c ** 2)));
    let last = Color(dark);
    for (let i = 0; i < 7; i++) {
        const newColor = last.mix(light, (1 / 8) * (i + 1) * gaus(i));
        shades.push(newColor.hex());
        last = newColor;
    }
    return shades;
}
function createScheme(color) {
    const hslcolor = new Color(color);
    const [, s] = [...hslcolor.hsl().array()];
    if (s < 20) {
        // Greyscale
        const mixColor = hslcolor.isLight() ? 'black' : 'white';
        return {
            passive: color,
            hover: hslcolor.mix(new Color(mixColor), 0.2).toString(),
            active: hslcolor.mix(new Color(mixColor), 0.25).toString(),
            text: getContrastingTextColor(color),
        };
    }
    else {
        const rate = (s - 50) / 250;
        return {
            passive: color,
            hover: hslcolor.mix(new Color('white'), rate).toString(),
            active: hslcolor.mix(new Color('black'), rate).toString(),
            text: getContrastingTextColor(color),
        };
    }
}
//// DEFAULTS ////
// Named Color Constants
export const Colors = {
    white: '#ffffff',
    black: '#151517',
    grey1: '#f5f5f5',
    grey2: '#e8e8e8',
    grey3: '#d0d0d1',
    grey4: '#a1a1a2',
    grey5: '#5b5b5b',
    grey6: '#313133',
    grey7: '#202022',
    green: '#23D79E',
    orange: '#FBA441',
    pink: '#FF0F80',
    blue: '#4C62F0',
    purple: '#5b0fd3',
    yellow: '#f4dc3e',
    red: '#f6464f',
};
const DefaultPaletteConfig = {
    primary: Colors.black,
    secondary: Colors.white,
    success: Colors.green,
    info: Colors.blue,
    warning: Colors.yellow,
    error: Colors.red,
    brand1: Colors.pink,
    brand2: Colors.purple,
    accent1: Colors.orange,
    accent2: Colors.blue,
};
export const DefaultPalette = createPalette(DefaultPaletteConfig);
export const DefaultSchemes = createSchemes(DefaultPaletteConfig);
export const getAvatarScheme = (identifier, theme) => {
    const { accent1, accent2, brand1, brand2, info, warning, error } = theme.schemes;
    const eligibleSchemes = [accent1, accent2, brand1, brand2, info, warning, error];
    const idHash = stringHash(identifier);
    return eligibleSchemes[idHash % eligibleSchemes.length];
};
export const mixColors = (color1, color2, weight) => {
    const mixWeight = weight !== undefined ? weight : 0.5;
    const Color1 = new Color(color1);
    const Color2 = new Color(color2);
    return Color1.mix(Color2, mixWeight).toString();
};
//# sourceMappingURL=Colors.js.map